import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { CalendarDays, FileText, Receipt, BadgeCheck, PalmtreeIcon, PartyPopper, Clock, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { conges_payes: "Congés payés", rtt: "RTT", maladie: "Maladie" };

function fmtDay(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long" }); }
  catch { return iso; }
}
function fmtDays(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
}

export default function TodayPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/stats/today").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="p-4 text-sm text-slate-500">Chargement…</div>;

  return (
    <div>
      <PageHeader
        title={`Bonjour${data.user_name ? `, ${data.user_name.split(" ")[0]}` : ""}`}
        subtitle="Votre journée en un coup d'œil."
      />

      {/* Situation actuelle */}
      {data.current_leave ? (
        <Card className="p-5 border-emerald-200 bg-emerald-50/60 mb-5" data-testid="today-on-leave">
          <div className="flex items-center gap-3">
            <PartyPopper className="h-6 w-6 text-emerald-700" />
            <div>
              <div className="font-heading font-semibold text-emerald-900">Vous êtes en congé aujourd'hui</div>
              <div className="text-sm text-emerald-800">{TYPE_LABEL[data.current_leave.type] || data.current_leave.type} · retour prévu le {fmtDay(data.current_leave.date_fin)}</div>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Soldes rapides */}
      <div className="grid grid-cols-2 gap-3 sm:gap-5 mb-5">
        <Card className="p-4 sm:p-5 border-slate-200" data-testid="today-cp">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Solde CP</div>
              <div className="mt-1 font-heading text-3xl font-bold text-[#0066FF]">{fmtDays(data.cp_balance)}</div>
              <div className="text-xs text-slate-500">jours disponibles</div>
            </div>
            <PalmtreeIcon className="h-5 w-5 text-[#0066FF]/40" />
          </div>
        </Card>
        <Card className="p-4 sm:p-5 border-slate-200" data-testid="today-rtt">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">RTT</div>
              <div className="mt-1 font-heading text-3xl font-bold text-violet-600">{fmtDays(data.rtt_balance)}</div>
              <div className="text-xs text-slate-500">jours disponibles</div>
            </div>
            <CalendarDays className="h-5 w-5 text-violet-300" />
          </div>
        </Card>
      </div>

      {/* Prochain congé */}
      {data.next_leave && (
        <Link to="/employe/conges" className="block mb-5">
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow" data-testid="today-next-leave">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-lg bg-[#0066FF]/10 flex items-center justify-center shrink-0">
                  <CalendarDays className="h-5 w-5 text-[#0066FF]" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">Prochain congé</div>
                  <div className="text-xs text-slate-500">{TYPE_LABEL[data.next_leave.type] || data.next_leave.type} · {fmtDay(data.next_leave.date_debut)} → {fmtDay(data.next_leave.date_fin)}</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
            </div>
          </Card>
        </Link>
      )}

      {/* Bulletins & attestations */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-5 mb-5">
        <Link to="/employe/bulletins" className="block">
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow" data-testid="today-payslip">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
                <Receipt className="h-5 w-5 text-emerald-700" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-slate-900">Dernier bulletin</div>
                <div className="text-xs text-slate-500 capitalize">{data.last_payslip_month || "Aucun bulletin"}</div>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/employe/attestations" className="block">
          <Card className="p-4 border-slate-200 hover:shadow-md transition-shadow" data-testid="today-certs">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-amber-700" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-slate-900">Attestations en cours</div>
                <div className="text-xs text-slate-500">{data.pending_certs} en attente · {data.pending_leaves} congé(s) à valider</div>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Docs pro expirants */}
      {data.expiring_docs && data.expiring_docs.length > 0 && (
        <Card className="p-4 sm:p-5 border-rose-200 bg-rose-50/40 mb-5" data-testid="today-expiring">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-rose-600" />
            <h3 className="font-heading font-semibold text-rose-900">Documents professionnels à surveiller</h3>
          </div>
          <div className="divide-y divide-rose-100">
            {data.expiring_docs.map((d, i) => (
              <div key={i} className="flex items-center justify-between py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{d.label}</div>
                  <div className="text-xs text-slate-500">Validité : {fmtDay(d.valid_until)}</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${d.days_left < 0 ? "bg-rose-200 text-rose-800" : d.days_left <= 7 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                  {d.days_left < 0 ? `expirée -${-d.days_left}j` : `dans ${d.days_left}j`}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Link to="/employe/documents-pro" className="text-[#0066FF] text-xs font-medium hover:underline">Voir tous mes documents →</Link>
          </div>
        </Card>
      )}

      <Card className="p-4 border-slate-200" data-testid="today-quick-actions">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-4 w-4 text-[#0066FF]" />
          <h3 className="font-heading font-semibold text-slate-900 text-sm">Actions rapides</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Link to="/employe/conges" className="rounded-lg border border-slate-200 p-3 text-center hover:bg-slate-50">
            <CalendarDays className="h-5 w-5 text-[#0066FF] mx-auto mb-1" />
            <div className="text-xs text-slate-700">Demander congé</div>
          </Link>
          <Link to="/employe/attestations" className="rounded-lg border border-slate-200 p-3 text-center hover:bg-slate-50">
            <FileText className="h-5 w-5 text-emerald-700 mx-auto mb-1" />
            <div className="text-xs text-slate-700">Attestation</div>
          </Link>
          <Link to="/employe/bulletins" className="rounded-lg border border-slate-200 p-3 text-center hover:bg-slate-50">
            <Receipt className="h-5 w-5 text-violet-700 mx-auto mb-1" />
            <div className="text-xs text-slate-700">Bulletins</div>
          </Link>
          <Link to="/employe/documents-pro" className="rounded-lg border border-slate-200 p-3 text-center hover:bg-slate-50">
            <BadgeCheck className="h-5 w-5 text-amber-700 mx-auto mb-1" />
            <div className="text-xs text-slate-700">Docs pro</div>
          </Link>
        </div>
      </Card>
    </div>
  );
}
